import { useRouter } from "next/router";
import { useEffect, useState } from "react";

type PointerActionType = "tap" | "long-press";

type PointerActionSequence = [PointerActionType, ...PointerActionType[]];

export const usePointerActionSequence = (
  sequence: PointerActionSequence,
  cb: (currentIndex: number) => void,
  timeout = 2000,
) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // Reset index on route change
    router.events.on("routeChangeComplete", () => {
      setCurrentIndex(0);
    });

    if (currentIndex === sequence.length) {
      cb(currentIndex - 1);
      setCurrentIndex(0);
      return;
    }

    if (currentIndex > 0) {
      timer = setTimeout(() => {
        setCurrentIndex(0);
      }, timeout);
    }

    const handlePointerDown = () => {
      const expectedAction = sequence[currentIndex];

      if (expectedAction === "tap") {
        setCurrentIndex((prev) => prev + 1);
      }

      let pressTimer: NodeJS.Timeout;
      if (expectedAction === "long-press") {
        pressTimer = setTimeout(() => {
          setCurrentIndex((prev) => prev + 1);
        }, 350); // Long press threshold
      }

      const handlePointerUp = () => {
        if (expectedAction === "long-press") {
          clearTimeout(pressTimer);
        }
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointerup", handlePointerUp);
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentIndex, sequence, cb, timeout]);
};
